import React from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Collapse,
  Fade,
  Paper,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import ParagraphCard from './ParagraphCard';

const ArticleParagraphs = ({
  article,
  selectedModel,
  setSelectedModel,
  models,
  settings,
  planLoading,
  onWriteAllParagraphs,
  onAddParagraph,
  onWriteParagraph,
  onEditParagraph,
  onGenerateImage,
  onDeleteParagraph,
  onImageClick,
  currentParagraphIndex,
  onRewriteComplete
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!article.paragraphs?.length) {
    return null;
  }

  const handleAddParagraphClick = (order) => {
    onAddParagraph({
      type: 'add',
      order,
      mode: 'dialog'
    });
  };

  // Check if this is a short post type
  const isShortPost = article.type === 'short_social_post';
  // Disable adding paragraphs if it's a short post and already has one paragraph
  const canAddParagraphs = !isShortPost || article.paragraphs.length === 0;

  return (
    <Box>
      {/* Header Section */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 0.5, sm: 1 }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { sm: 'center' },
          gap: { xs: 1.5, sm: 2 }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Typography variant="h6" sx={{ 
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              fontWeight: 600,
              color: 'text.primary'
            }}>
              {isShortPost ? 'Content' : `Paragraphs (${article.paragraphs.length})`}
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row' },
            gap: 1,
            width: '100%',
            maxWidth: { sm: '500px' }
          }}>
            <FormControl 
              size="small" 
              sx={{ 
                flex: { xs: '1.5', sm: '1.5' },
                minWidth: { xs: 0, sm: 'auto' }
              }}
            >
              <InputLabel>AI Model</InputLabel>
              <Select
                value={selectedModel}
                label="AI Model"
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={planLoading}
                endAdornment={
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <Tooltip title="Set default model in project settings">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/settings?highlight=default_paragraph_writer_model`);
                        }}
                        sx={{ 
                          color: 'primary.main',
                          p: 0.5,
                          display: { xs: 'none', sm: 'flex' }
                        }}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: '300px'
                    }
                  }
                }}
              >
                {models.map((model) => (
                  <MenuItem 
                    key={model.name} 
                    value={model.name}
                    sx={{ 
                      py: 1,
                      minHeight: '32px'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: '0.875rem'
                    }}>
                      <span>{model.name} ({model.provider})</span>
                      {model.name === settings?.default_paragraph_writer_model && (
                        <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                          (Default)
                        </Typography>
                      )}
                      {model.premium && (
                        <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!isShortPost && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<AutoStoriesIcon />}
                onClick={onWriteAllParagraphs}
                disabled={planLoading}
                sx={{
                  flex: { xs: '1', sm: '1' },
                  height: '36px',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 }
                }}
              >
                {isMobile ? 'Write All' : 'Write All Paragraphs'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Paragraphs List */}
      <Stack spacing={{ xs: 2, sm: 3 }}>
        {canAddParagraphs && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddParagraphClick(1)}
            fullWidth
            sx={{ 
              height: { xs: '48px', sm: '40px' },
              borderStyle: 'dashed',
              borderWidth: '1px',
              '&:hover': {
                borderStyle: 'dashed',
                borderWidth: '1px'
              }
            }}
          >
            Add Paragraph Here
          </Button>
        )}

        {article.paragraphs.map((paragraph, index) => (
          <React.Fragment key={paragraph.id}>
            <Fade in={true} timeout={500}>
              <Collapse in={true} timeout={500}>
                <ParagraphCard
                  paragraph={paragraph}
                  onEditParagraph={onEditParagraph}
                  onWriteParagraph={onWriteParagraph}
                  onGenerateImage={onGenerateImage}
                  onDeleteParagraph={onDeleteParagraph}
                  onImageClick={onImageClick}
                  loading={planLoading}
                  articleId={article.id}
                  models={models}
                  settings={settings}
                  selectedModel={selectedModel}
                  setSelectedModel={setSelectedModel}
                  onRewriteComplete={onRewriteComplete}
                  currentlyWriting={currentParagraphIndex === paragraph.id}
                  article={article}
                />
              </Collapse>
            </Fade>
            
            {canAddParagraphs && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleAddParagraphClick(paragraph.order + 1)}
                fullWidth
                sx={{ 
                  height: { xs: '48px', sm: '40px' },
                  borderStyle: 'dashed',
                  borderWidth: '1px',
                  '&:hover': {
                    borderStyle: 'dashed',
                    borderWidth: '1px'
                  }
                }}
              >
                Add Paragraph Here
              </Button>
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};

export default ArticleParagraphs; 